import { Duration } from "luxon";
import type { SharedApiV2 } from "@capstone/mock-api";
import { HistoryEntry } from "mock-api/src/managed-receiving-global/types";
import {
  AppointmentInterval,
  AppointmentIntervalDurations,
} from "src/app/core/constants";
import { ChangesHistory } from "src/app/core/history.model";
import { formatWeekdays } from "src/app/core/pipes/weekdays.pipe";
import { Site } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import {
  getApiDetailsDecorator,
  getEnumMember,
  isExistent,
  parseDuration,
} from "src/utils";
import {
  DailyDockCapacityExceptionV2,
  DailyDockCapacityV2,
} from "./daily-dock-capacity-v2.model";
import { DockGroup } from "./dock-group.model";
import { DockReference, getDockRouteUrl } from "./dock.model";
import {
  HourlyDockCapacityExceptionV2,
  HourlyDockCapacityV2,
} from "./hourly-dock-capacity-v2.model";
import { ScheduleCutoffTimeExceptionV2 } from "./schedule-cutoff-time-exception-v2.model";

const api = getApiDetailsDecorator<SharedApiV2.DockDto>();

export class DockV2 {
  private constructor(args: ClassProperties<DockV2>) {
    this.address = args.address;
    this.appointmentInterval = args.appointmentInterval;
    this.dailyCapacities = args.dailyCapacities;
    this.earlyScheduleThreshold = args.earlyScheduleThreshold;
    this.gatePassMessage = args.gatePassMessage;
    this.group = args.group;
    this.history = args.history;
    this.hourlyCapacities = args.hourlyCapacities;
    this.id = args.id;
    this.isActive = args.isActive;
    this.isDockLevelSettingEnabled = args.isDockLevelSettingEnabled;
    this.isReservationSlotsOutsideOfHourlyCapacityTimeWindowPresented =
      args.isReservationSlotsOutsideOfHourlyCapacityTimeWindowPresented;
    this.isReservationsInThreshold = args.isReservationsInThreshold;
    this.lateScheduleThreshold = args.lateScheduleThreshold;
    this.maximumAppointmentDuration = args.maximumAppointmentDuration;
    this.minimumAppointmentDuration = args.minimumAppointmentDuration;
    this.name = args.name;
    this.scheduleCutoffTime = args.scheduleCutoffTime;
    this.scheduleCutoffTimeExceptions = args.scheduleCutoffTimeExceptions;
    this.site = args.site;
    this.weekdays = args.weekdays;
  }

  @api({ key: "capacities" }) public readonly dailyCapacities: ReadonlyArray<
    DailyDockCapacityV2 | DailyDockCapacityExceptionV2
  >;
  @api() public readonly history: readonly ChangesHistory[];
  @api() public readonly hourlyCapacities: ReadonlyArray<
    HourlyDockCapacityV2 | HourlyDockCapacityExceptionV2
  >;
  @api({ key: "dockCutoffExceptions" })
  public readonly scheduleCutoffTimeExceptions: readonly ScheduleCutoffTimeExceptionV2[];
  @api({ key: "dockGroup" }) public readonly group: DockGroup;
  @api() public readonly id: DockReference["id"];
  @api() public readonly address: string | null;
  @api() public readonly appointmentInterval: Duration | null;
  @api() public readonly earlyScheduleThreshold: number;
  @api() public readonly gatePassMessage: string | null;
  @api({ key: "active" }) public readonly isActive: boolean;
  @api({ key: "enableDockLevelSetting" })
  public readonly isDockLevelSettingEnabled: boolean | null;
  @api({ key: "reservationsInThreshold" })
  public readonly isReservationsInThreshold: boolean;
  @api({ key: "presentReservationSlotsOutsideHourlyCapacityTimeWindow" })
  public readonly isReservationSlotsOutsideOfHourlyCapacityTimeWindowPresented: boolean;
  @api() public readonly lateScheduleThreshold: number;
  @api({ key: "maxAppointmentDuration" })
  public readonly maximumAppointmentDuration: Duration | null;
  @api({ key: "minimumUnloadMinutes" })
  public readonly minimumAppointmentDuration: Duration | null;
  @api() public readonly name: string;
  @api() public readonly scheduleCutoffTime: TimeOfDay | null;
  @api({ key: "daysOfWeek" }) public readonly weekdays: Weekdays;
  public readonly site: Site;

  public static deserialize(
    data: SharedApiV2.DockDto,
    { site }: DeserializeArgs,
  ): DockV2 {
    const appointmentInterval =
      // Get the same object so that the radio input comparison in the dock site
      // settings form matches properly for showing which is checked.
      AppointmentIntervalDurations[
        getEnumMember(
          AppointmentInterval,
          data.appointmentInterval,
          AppointmentInterval.Invalid,
        )
      ] ?? parseDuration(data.appointmentInterval, "minutes");

    return new DockV2({
      address: data.address ?? null,
      appointmentInterval,
      scheduleCutoffTimeExceptions:
        data.dockCutoffExceptions?.map((exception) =>
          ScheduleCutoffTimeExceptionV2.deserialize(exception, { site }),
        ) ?? [],
      dailyCapacities:
        data.capacities?.map((capacity) =>
          DailyDockCapacityV2.deserialize(capacity, { site }),
        ) ?? [],
      earlyScheduleThreshold: data.earlyScheduleThreshold,
      group: data.dockGroup,
      gatePassMessage: data.gatePassMessage || null,
      history: data.history ? ChangesHistory.deserializeList(data.history) : [],
      hourlyCapacities:
        data.hourlyCapacities?.map((capacity) =>
          HourlyDockCapacityV2.deserialize(capacity, { site }),
        ) ?? [],
      id: data.id,
      isActive: data.active,
      isDockLevelSettingEnabled: data.enableDockLevelSetting,
      isReservationsInThreshold: data.reservationsInThreshold,
      isReservationSlotsOutsideOfHourlyCapacityTimeWindowPresented:
        data.presentReservationSlotsOutsideHourlyCapacityTimeWindow,
      lateScheduleThreshold: data.lateScheduleThreshold,
      maximumAppointmentDuration: isExistent(data.maxAppointmentDuration)
        ? parseDuration(data.maxAppointmentDuration, "minutes")
        : null,
      minimumAppointmentDuration: isExistent(data.minimumUnloadMinutes)
        ? parseDuration(data.minimumUnloadMinutes, "minutes")
        : null,
      name: data.name,
      scheduleCutoffTime: data.scheduleCutoffTime
        ? TimeOfDay.deserialize(data.scheduleCutoffTime)
        : null,
      site,
      weekdays: Weekdays.deserialize(data.daysOfWeek),
    });
  }

  public static deserializeDockCapacityHistory(
    data: HistoryEntry[],
  ): readonly ChangesHistory[] {
    return ChangesHistory.deserializeList(data, {
      fieldDefinitions: {
        dayOfWeek: {
          label: "Days",
          formatChangeValue: (value) =>
            value ? formatWeekdays(Weekdays.deserialize(value), "short") : null,
        },
      },
    });
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getDockRouteUrl(this.site, this, ...childPaths);
  }
}

interface DeserializeArgs {
  readonly site: Site;
}

export interface SimpleDock extends Pick<
  DockV2,
  | "address"
  | "appointmentInterval"
  | "hourlyCapacities"
  | "isDockLevelSettingEnabled"
  | "id"
  | "name"
  | "minimumAppointmentDuration"
  | "maximumAppointmentDuration"
  | "gatePassMessage"
  | "site"
> {
  getRouteUrl(...childPaths: string[]): string;
}

export function deserializeSimpleDock(
  data: SharedApiV2.GetDockDto,
  { site }: DeserializeArgs,
): SimpleDock {
  const appointmentInterval =
    AppointmentIntervalDurations[
      getEnumMember(
        AppointmentInterval,
        data.appointmentInterval,
        AppointmentInterval.Invalid,
      )
    ] ?? parseDuration(data.appointmentInterval, "minutes");
  return {
    address: data.address ?? null,
    appointmentInterval,
    hourlyCapacities:
      data.hourlyCapacities?.map((capacity) =>
        HourlyDockCapacityV2.deserialize(capacity, { site }),
      ) ?? [],
    id: data.id,
    isDockLevelSettingEnabled: data.enableDockLevelSetting,
    gatePassMessage: data.gatePassMessage || null,
    maximumAppointmentDuration: isExistent(data.maxAppointmentDuration)
      ? parseDuration(data.maxAppointmentDuration, "minutes")
      : null,
    minimumAppointmentDuration: isExistent(data.minimumUnloadMinutes)
      ? parseDuration(data.minimumUnloadMinutes, "minutes")
      : null,
    name: data.name,
    site,
    getRouteUrl(...childPaths: string[]): string {
      return getDockRouteUrl(site, this, ...childPaths);
    },
  };
}
